import { LiveMatch } from "./types";
import { EXCLUDE_SPORT_NAME } from "./adapter";

/** 5 minutes. The live full-feed runs at 45s (FULLFEED_INTERVAL_MS); a fixture
 *  list for the next 24 hours does not need one sixth of that rate, and the
 *  binding constraint on the API box is request CAPACITY, not per-request cost. */
export const PREMATCH_INTERVAL_MS = 300_000;

/** 7 days, in minutes — matches the collector lead (168h) and factserve's future
 *  window, not the old 24h cap. Mirrored by the server's own bound in the
 *  /suggestions pool merge, which re-checks it against a cached board that ages. */
export const HORIZON_MINUTES = 10080;

/**
 * Pulls the operator's pre-match board on its own slow clock and posts it.
 *
 * ⛔ Deliberately NOT part of DigitainFeedSource. Different cadence, different
 * failure mode, and it must never be able to stall the live feed — the live
 * board is what the widget shows today, and a pre-match pull that hangs or
 * throws must cost nothing.
 *
 * It does NOT touch page_context.live_matches. The /suggestions payload does
 * not grow by a byte; the board goes to /api/companion/prematch, which the
 * server caches for 15 minutes and reads on the hot path.
 */
export class PrematchSource {
  private timer: ReturnType<typeof setInterval> | null = null;
  private board: LiveMatch[] = [];

  private inFlightGeneration: number | null = null;
  private generation = 0;

  constructor(
    // May be async: on the Digitain `replay` route a pull is a fan-out of real
    // requests against the operator's own API (group → tournaments → events),
    // not a read of something already in memory.
    private pull: () => LiveMatch[] | Promise<LiveMatch[]>,
    private post: (board: LiveMatch[]) => Promise<boolean>,
  ) {}

  start(): void {
    if (this.timer != null) return; // single timer — a re-start must not stack
    this.generation++;
    this.timer = setInterval(() => this.tick(), PREMATCH_INTERVAL_MS);
  }

  stop(): void {
    this.generation++;
    this.inFlightGeneration = null;
    if (this.timer != null) clearInterval(this.timer);
    this.timer = null;
  }

  getBoard(): LiveMatch[] {
    return this.board;
  }

  private tick(): void {
    // ⛔ One pull at a time. The fan-out is a chain of round-trips against the
    // operator's production API; if one ever outlives the 5-minute timer, the
    // answer is to skip that tick, not to stack a second fan-out on top of it.
    if (this.inFlightGeneration === this.generation) return;
    const generation = this.generation;
    this.inFlightGeneration = generation;
    Promise.resolve()
      .then(() => this.pull())
      .then((raw) => this.publish(raw || [], generation))
      // Degrade silently: a throwing or rejecting pull leaves the widget behaving
      // exactly as it does today.
      .catch(() => undefined)
      .then(() => {
        if (this.inFlightGeneration === generation) this.inFlightGeneration = null;
      });
  }

  private publish(raw: LiveMatch[], generation: number): void {
    if (generation !== this.generation || this.timer == null) return;
    const now = Date.now();
    const kept = raw.filter((m) => {
      if (!m.starts_at) return false;
      const t = Date.parse(m.starts_at);
      if (Number.isNaN(t)) return false;
      const minutes = (t - now) / 60000;
      // Already started, or past the horizon.
      if (minutes < 0 || minutes > HORIZON_MINUTES) return false;
      // Same virtual/e-sport gate the live feed applies, on the ALWAYS-ENGLISH
      // name — testing the localized name matched nothing on a non-English
      // board and pulled e-sports straight into the pool.
      return !EXCLUDE_SPORT_NAME.test(String(m.sport_en || m.sport || ""));
    });
    if (generation !== this.generation || this.timer == null) return;
    this.board = kept;
    // Fire-and-forget, but the rejection is CAUGHT: an unhandled rejection from
    // inside a setInterval callback surfaces in the operator's own console and
    // reads as our widget crashing on their site.
    try {
      void this.post(kept).catch(() => undefined);
    } catch {
      /* never break the widget's own loop */
    }
  }
}
